import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCities } from "../lib/useCities";
import CenterCard from "../components/CenterCard";
import { Search as SearchIcon } from "lucide-react";

export default function Search() {
    const [params, setParams] = useSearchParams();
    const city = params.get("city") || "";
    const q = params.get("q") || "";
    const [centers, setCenters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qValue, setQValue] = useState(q);
    const { cities: cityNames } = useCities();

    useEffect(() => {
        setLoading(true);
        const p = {};
        if (city) p.city = city;
        if (q) p.q = q;
        api.get("/centers", { params: p })
            .then((r) => setCenters(r.data))
            .finally(() => setLoading(false));
    }, [city, q]);

    const updateCity = (c) => {
        const n = new URLSearchParams(params);
        if (c) n.set("city", c); else n.delete("city");
        setParams(n);
    };

    const submitQ = (e) => {
        e.preventDefault();
        const n = new URLSearchParams(params);
        if (qValue) n.set("q", qValue); else n.delete("q");
        setParams(n);
    };

    return (
        <div className="pt-24 pb-24 min-h-screen">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-start justify-between flex-wrap gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">Browse</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">
                            {city ? city : "All cities"}
                        </h1>
                    </div>
                    <form onSubmit={submitQ} className="flex items-center bg-white border border-zinc-300 h-12 min-w-[280px]">
                        <SearchIcon className="h-4 w-4 text-zinc-500 ml-3" strokeWidth={1.5} />
                        <input
                            data-testid="search-address-input"
                            value={qValue}
                            onChange={(e) => setQValue(e.target.value)}
                            placeholder="Search by address or center…"
                            className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none"
                        />
                        <button type="submit" data-testid="search-submit-btn" className="h-full px-4 bg-zinc-950 text-white text-sm">Search</button>
                    </form>
                </div>

                <div className="mt-10 flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="city-filter-all"
                        onClick={() => updateCity("")}
                        className={`px-3 py-1.5 text-xs uppercase tracking-label border ${!city ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"}`}
                    >
                        All
                    </button>
                    {cityNames.map((c) => (
                        <button
                            key={c}
                            data-testid={`city-filter-${c}`}
                            onClick={() => updateCity(c)}
                            className={`px-3 py-1.5 text-xs uppercase tracking-label border ${city === c ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                <div className="mt-10">
                    {loading ? (
                        <div className="text-zinc-500 text-sm tracking-label uppercase">Loading…</div>
                    ) : centers.length === 0 ? (
                        <div data-testid="no-results" className="border border-zinc-200 p-12 text-center bg-zinc-50">
                            <div className="font-display text-2xl font-semibold">No addresses yet in {city || "this area"}</div>
                            <p className="mt-2 text-zinc-600 text-sm">Try another city — or know an operator? <Link to="/enroll" className="underline">Invite them to enroll.</Link></p>
                        </div>
                    ) : (
                        <div data-testid="results-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {centers.map((c, i) => (
                                <CenterCard key={c.center_id} center={c} index={i} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
